import { Link } from '@tanstack/react-router'
import { TrendingUp, Sparkles, ExternalLink } from 'lucide-react'
import { Card, CardTitle, CardDescription, Button, Badge } from '@/components/ui'
import { ImprovementCard } from '@/components/ImprovementCard'
import { useAppImprovements, useGenerateImprovementAudit } from '@/hooks/useImprovements'
import type { AppRecord } from '@/lib/types'
import { formatRelativeDate } from '@/lib/utils'

/** Compact improvements panel for the AppDetail page. */
export function ImprovementsInline({ app }: { app: AppRecord }) {
  const { items, loading } = useAppImprovements(app.slug)
  const audit = useGenerateImprovementAudit()

  const open = items.filter(i => i.status === 'suggested' || i.status === 'in-progress')
  const high = open.filter(i => i.impact === 'high')
  const top = open
    .sort((a, b) => {
      const o = { high: 0, medium: 1, low: 2 } as const
      return o[a.impact] - o[b.impact]
    })
    .slice(0, 4)

  const lastAudited = app.improvement_audit?.last_audited_at
  const iteration = app.improvement_audit?.iteration ?? 0

  return (
    <Card className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow mb-1">Value Engineering</div>
          <CardTitle>Concrete moves that raise this app's exit value</CardTitle>
          <CardDescription className="mt-1">
            {iteration === 0
              ? 'No audit run yet — Gemini will surface 6-12 specific improvements with copy-paste-ready prompts.'
              : <>Iteration <span className="text-gold-soft">{iteration}</span> · last audited{' '}
                <span className="text-gold-soft">{lastAudited ? formatRelativeDate(lastAudited) : '—'}</span> · {open.length} open ({high.length} high impact)</>}
          </CardDescription>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <Link
            to="/value"
            className="inline-flex items-center gap-2 rounded-md font-medium transition-all duration-150 tracking-wide font-body px-3 py-1.5 text-xs bg-transparent text-cream hover:bg-cream/5 border border-cream/20 hover:border-cream/40"
          >
            <TrendingUp size={12} /> Open full board
            <ExternalLink size={10} />
          </Link>
          <Button
            variant="gold-outline"
            size="sm"
            loading={audit.isPending}
            onClick={() => audit.mutate(app.slug)}
          >
            <Sparkles size={12} />
            {iteration === 0 ? 'Run audit' : 'Re-audit'}
          </Button>
        </div>
      </div>

      <div className="divider-editorial" />

      {loading ? (
        <div className="text-cream/55 text-sm">Loading…</div>
      ) : open.length === 0 && items.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-bronze/30 rounded-lg">
          <TrendingUp size={28} className="text-bronze/60 mx-auto mb-3" />
          <p className="font-body text-cream/65 text-sm max-w-md mx-auto">
            Click <em>Run audit</em> to generate concrete improvement suggestions for this app.
            Each suggestion comes with a copy-paste-ready LLM prompt to execute it.
          </p>
        </div>
      ) : open.length === 0 ? (
        <div className="bg-success/8 border border-success/30 rounded-lg p-4">
          <p className="font-body text-cream/85 text-sm">
            All open suggestions resolved. <strong className="text-success">{items.filter(i => i.status === 'done').length} items captured.</strong>
            {' '}Re-audit to surface the next round.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone="gold">{open.length} open</Badge>
            {high.length > 0 && <Badge tone="warning">{high.length} HIGH-impact</Badge>}
            <Badge tone="success">{items.filter(i => i.status === 'done').length} done</Badge>
            {items.filter(i => i.status === 'dismissed').length > 0 && (
              <Badge tone="cream">{items.filter(i => i.status === 'dismissed').length} dismissed</Badge>
            )}
          </div>
          <div className="space-y-2">
            {top.map(item => <ImprovementCard key={item.id} item={item} />)}
          </div>
          {open.length > top.length && (
            <Link to="/value" className="block text-center font-mono text-xs text-bronze hover:text-gold-soft uppercase tracking-[0.12em] py-2">
              {open.length - top.length} more · open full board →
            </Link>
          )}
        </>
      )}

      {app.improvement_audit?.summary && (
        <div className="bg-navy/40 border border-bronze/15 rounded-md p-4">
          <div className="eyebrow mb-2">Audit summary</div>
          <p className="font-body text-sm text-cream/75 italic leading-relaxed">{app.improvement_audit.summary}</p>
        </div>
      )}
    </Card>
  )
}
